import api from '../api';
import '../features/attendeeDocuments/bootstrap';
import { saveAttendanceWithRecovery } from './attendancePersistence';

const AttendeeManagementService = {
  recordReferenceData: () => api.get('/attendee-management/reference-data').then((response) => response.data),
  bootstrapFast: () => api.get('/attendee-management/bootstrap-fast', { skipTransientRetry: true }).then((r) => r.data),
  dashboard: () => api.get('/attendee-management/dashboard').then((r) => r.data),
  getConsultationPreferences: () => api.get('/users/me/consultation-preferences/attendees', { skipGlobalErrorModal: true }).then((r) => r.data),
  saveConsultationPreferences: (payload) => api.put('/users/me/consultation-preferences/attendees', payload, { skipGlobalErrorModal: true }).then((r) => r.data),
  teachers: () => api.get('/attendee-management/teachers').then((r) => r.data),
  listGroups: () => api.get('/attendee-management/groups').then((r) => r.data),
  saveGroup: (payload) => (payload.id ? api.put(`/attendee-management/groups/${payload.id}`, payload) : api.post('/attendee-management/groups', payload)).then((r) => r.data),
  deleteGroup: (id) => api.delete(`/attendee-management/groups/${id}`).then((r) => r.data),
  listClasses: () => api.get('/attendee-management/classes').then((r) => r.data),
  saveClass: (payload) => (payload.id ? api.put(`/attendee-management/classes/${payload.id}`, payload) : api.post('/attendee-management/classes', payload)).then((r) => r.data),
  deleteClass: (id) => api.delete(`/attendee-management/classes/${id}`).then((r) => r.data),
  listAttendees: (params = {}) => api.get('/attendee-management/attendees', { params }).then((r) => r.data),
  getAttendee: (id) => api.get(`/attendee-management/attendees/${id}`).then((r) => r.data),
  listDiaryRoster: () => api.get('/attendee-management/diary-roster').then((r) => r.data),
  getAttendee: (id, params = {}) => api.get(`/attendee-management/attendees/${id}`, { params }).then((r) => r.data),
  saveAttendee: (payload) => (payload.id ? api.put(`/attendee-management/attendees/${payload.id}`, payload) : api.post('/attendee-management/attendees', payload)).then((r) => r.data),
  deleteAttendee: (id) => api.delete(`/attendee-management/attendees/${id}`).then((r) => r.data),
  saveAbsenceJustification: (id, payload) => api.post(`/attendee-management/attendees/${id}/absence-justifications`, payload).then((r) => r.data),
  updateAbsenceJustification: (id, justificationId, payload) => api.put(`/attendee-management/attendees/${id}/absence-justifications/${encodeURIComponent(justificationId)}`, payload).then((r) => r.data),
  deleteAbsenceJustification: (id, justificationId) => api.delete(`/attendee-management/attendees/${id}/absence-justifications/${encodeURIComponent(justificationId)}`).then((r) => r.data),
  listAttendeeHistory: (id) => api.get(`/attendee-management/attendees/${id}/history`).then((r) => r.data),
  listIncidents: () => api.get('/attendee-management/incidents').then((r) => r.data),
  saveIncident: (payload) => (payload.id ? api.put(`/attendee-management/incidents/${payload.id}`, payload) : api.post('/attendee-management/incidents', payload)).then((r) => r.data),
  deleteIncident: (id) => api.delete(`/attendee-management/incidents/${id}`).then((r) => r.data),
  listAttendance: (params = {}) => api.get('/attendee-management/attendance', { params }).then((r) => r.data),
  saveAttendance: (payload) => saveAttendanceWithRecovery({
    api,
    endpoint: '/attendee-management/attendance',
    payload,
    entryIdField: 'attendeeId',
  }),
  deleteAttendance: (id) => api.delete(`/attendee-management/attendance/${id}`).then((r) => r.data),
  listLessonPlans: () => api.get('/attendee-management/lesson-plans').then((r) => r.data),
  saveLessonPlan: (payload) => api.post('/attendee-management/lesson-plans', payload).then((r) => r.data),
  deleteLessonPlan: (id) => api.delete(`/attendee-management/lesson-plans/${id}`).then((r) => r.data),
  getSettings: (params = {}) => api.get('/attendee-management/settings', { params }).then((r) => r.data),
  saveSettings: (payload) => api.put('/attendee-management/settings', payload).then((r) => r.data),
  listDocumentTemplates: () => api.get('/attendee-management/document-templates').then((r) => r.data),
  saveDocumentTemplate: (payload) => (payload.id ? api.put(`/attendee-management/document-templates/${payload.id}`, payload) : api.post('/attendee-management/document-templates', payload)).then((r) => r.data),
  deleteDocumentTemplate: (id) => api.delete(`/attendee-management/document-templates/${id}`).then((r) => r.data),
  listIntegrationSyncIssues: async ({ unsurfaced = false } = {}) => (await api.get('/api/attendee-management/integration-sync/issues', { params: { unsurfaced } })).data,
  markIntegrationSyncIssueSurfaced: async (issueKey) => (await api.post(`/api/attendee-management/integration-sync/issues/${encodeURIComponent(issueKey)}/surfaced`)).data,
  runIntegrationSync: async () => (await api.post('/api/attendee-management/integration-sync/run')).data,

};


// AttendeeManagementService-memory-cache-20260915
const __managementCache = new Map();
const __managementInflight = new Map();
const __cacheKey = (name, args) => name + ':' + JSON.stringify(args || []);
const __cachedCall = (name, ttl, original) => (...args) => {
  const key = __cacheKey(name, args);
  const now = Date.now();
  const hit = __managementCache.get(key);
  if (hit && now - hit.at < ttl) return Promise.resolve(hit.value);
  if (__managementInflight.has(key)) return __managementInflight.get(key);
  const pending = Promise.resolve(original(...args)).then((value) => {
    __managementCache.set(key, { at: Date.now(), value });
    __managementInflight.delete(key);
    return value;
  }).catch((error) => { __managementInflight.delete(key); throw error; });
  __managementInflight.set(key, pending);
  return pending;
};
const __invalidateManagement = (...prefixes) => {
  for (const key of Array.from(__managementCache.keys())) if (prefixes.some((prefix) => key.startsWith(prefix + ':'))) __managementCache.delete(key);
};
[
  ['dashboard', 120000], ['teachers', 300000], ['listCourses', 300000], ['listClasses', 180000],
  ['listStudents', 180000], ['listAttendees', 180000], ['listIncidents', 45000], ['listAttendance', 45000], ['getSettings', 300000],
].forEach(([name, ttl]) => {
  if (typeof AttendeeManagementService[name] === 'function') AttendeeManagementService[name] = __cachedCall(name, ttl, AttendeeManagementService[name]);
});
[
  ['saveStudent',['listStudents','dashboard']], ['deleteStudent',['listStudents','dashboard']],
  ['saveAttendee',['listAttendees','dashboard']], ['deleteAttendee',['listAttendees','dashboard']],
  ['saveClass',['listClasses','dashboard']], ['deleteClass',['listClasses','dashboard']],
  ['saveCourse',['listCourses','dashboard']], ['deleteCourse',['listCourses','dashboard']],
  ['saveAttendance',['listAttendance','dashboard']], ['deleteAttendance',['listAttendance','dashboard']],
  ['saveIncident',['listIncidents']], ['deleteIncident',['listIncidents']], ['saveSettings',['getSettings']],
].forEach(([name, prefixes]) => {
  if (typeof AttendeeManagementService[name] !== 'function') return;
  const original=AttendeeManagementService[name];
  AttendeeManagementService[name]=(...args)=>Promise.resolve(original(...args)).then((value)=>{__invalidateManagement(...prefixes);return value;});
});

export default AttendeeManagementService;
